import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../controls/buttons/FooterModalButtons";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import {
  CATALOG_MATERIAL_INITIAL_VALUES,
  CatalogMaterial,
} from "../../../models/CatalogMaterial";
import {
  deleteCatalogMaterial,
  upsertCatalogMaterial,
} from "../../../repositories/CatalogMaterialRepository";
import { modalStyles } from "@/shared/styles/modalStyles";
import { validateNoSpecialChars } from "../../validates";
import { useCatalogMaterialContext } from "../context/CatalogMaterialContext";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  material: CatalogMaterial | null;
  showModal: "new" | "edit" | "delete" | "";
  onHide: ({ entityId, shouldUpdate }: onModalHideProps) => void;
  refreshData: Function;
};
export const MaterialModal = ({
  material,
  showModal,
  onHide,
  refreshData,
}: Props) => {
  const { state } = useCatalogMaterialContext();

  const [notificationMessage, setNotificationMessage] = useState("");
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [title, setTitle] = useState("");

  const {
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<CatalogMaterial>({
    defaultValues: CATALOG_MATERIAL_INITIAL_VALUES,
  });

  const isFormDirty =
    !(Object.keys(dirtyFields).length === 0) || showModal == "new";

  const validateOnlyNumbers = (value: number | null) => {
    const numericValue = Number(value);

    if (value === null) return "Value cannot be null";
    if (
      state.materials?.some(
        (m: CatalogMaterial) =>
          m.L1MaterialId === numericValue &&
          m.MaterialId !== material?.MaterialId,
      )
    ) {
      return "ID already exists in materials";
    }
    return !isNaN(value) || "Only numbers are allowed";
  };

  const onSubmit: SubmitHandler<CatalogMaterial> = async (
    data: CatalogMaterial,
  ) => {
    setIsSubmitLoading(true);
    const materials: CatalogMaterial[] = state.materials || [];
    if (showModal === "delete") {
      setIsSubmitLoading(true);
      const resp = await deleteCatalogMaterial(material?.MaterialId ?? null);
      if (resp.ok) {
        await refreshData();
        onHide({ shouldUpdate: true });
        setNotificationMessage("The material was deleted successfully");
      } else {
        setError(resp.message);
      }
      setIsSubmitLoading(false);
    } else {
      const newName = data.MaterialName.trim().toLowerCase();
      if (
        materials.some(
          (material) =>
            material.MaterialName.trim().toLowerCase() === newName &&
            material.MaterialId !== data.MaterialId,
        )
      ) {
        setError(
          `A material with the name '${data.MaterialName.trim()}' already exists.`,
        );
        setIsSubmitLoading(false);
      } else {
        await performUpsert(data);
      }
    }
  };

  const performUpsert = async (data: CatalogMaterial) => {
    const resp = await upsertCatalogMaterial(data);
    if (resp.ok) {
      const entityId = get(resp, "data.returnValue", 0);
      await refreshData();
      setNotificationMessage("The material was saved successfully");
      onHide({ entityId: entityId, shouldUpdate: true });
    } else {
      setError(resp.message);
      onHide({ shouldUpdate: false });
    }
    setIsSubmitLoading(false);
  };

  useEffect(() => {
    if (showModal != "") {
      setTitle(showModal.toUpperCase());
      reset(material || CATALOG_MATERIAL_INITIAL_VALUES);
    }
  }, [showModal]);

  return (
    <>
      <MesfModal
        title={`${title} MATERIAL`}
        open={showModal != ""}
        handleClose={() => onHide({ shouldUpdate: false })}
        id="material-modal"
        maxWidth="sm"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content dividers style={{ padding: "15px 30px" }}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={9}>
                <Controller
                  name="MaterialName"
                  control={control}
                  rules={{
                    required: "Name is required",
                    validate: validateNoSpecialChars,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      label="Name"
                      variant="outlined"
                      fullWidth
                      margin="dense"
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={3}>
                <Controller
                  name="L1MaterialId"
                  control={control}
                  rules={{
                    required: "Material Id is required",
                    validate: validateOnlyNumbers,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="L1 MaterialId"
                      value={field.value}
                      onChange={field.onChange}
                      variant="outlined"
                      fullWidth
                      margin="dense"
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <Controller
                  name="IsAlloy"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      sx={modalStyles.checkbox}
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                          name="IsAlloy"
                          color="primary"
                        />
                      }
                      label="Is Alloy"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
                <Controller
                  name="UseInEAF"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      sx={modalStyles.checkbox}
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                          name="UseInEAF"
                          color="primary"
                        />
                      }
                      label="Use in EAF"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
                <Controller
                  name="UseInLF"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      sx={modalStyles.checkbox}
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                          name="UseInLF"
                          color="primary"
                        />
                      }
                      label="Use in LF"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <Controller
                  name="Enabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={field.onChange}
                        />
                      }
                      label="Enabled"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </MesfModal.Content>
          <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
            <FooterModalButtons
              isSubmitLoading={isSubmitLoading}
              onHide={() => onHide({ shouldUpdate: false })}
              buttonLabel={showModal === "delete" ? "Delete" : "Save"}
              buttonColor={showModal === "delete" ? "secondary" : "primary"}
              disabled={!isFormDirty && showModal !== "delete"}
            />
          </MesfModal.Actions>
        </form>
      </MesfModal>
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error with Material"
      />
    </>
  );
};
